import { useEffect, useState } from "react";
import { FileSearch, KeySquare, Newspaper, BarChart3, RefreshCw, Check, X, Copy, Sparkles, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeoDashboard = ({ adminKey }) => {
  const headers = { "X-Admin-Key": adminKey, "Content-Type": "application/json" };
  const [analyses, setAnalyses] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [articles, setArticles] = useState([]);
  const [gsc, setGsc] = useState(null);
  const [busy, setBusy] = useState("");
  const [newKw, setNewKw] = useState("");
  const [genKw, setGenKw] = useState("");
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const [a, k, ar, g] = await Promise.all([
        fetch(`${API}/seo/analyses`, { headers }).then((r) => r.json()),
        fetch(`${API}/seo/keywords`, { headers }).then((r) => r.json()),
        fetch(`${API}/seo/articles`, { headers }).then((r) => r.json()),
        fetch(`${API}/seo/gsc/performance`, { headers }).then((r) => r.json()),
      ]);
      setAnalyses(a);
      setKeywords(k);
      setArticles(ar);
      setGsc(g);
      if (!genKw && k.length) setGenKw(k.find((x) => x.approved)?.keyword || "");
    } catch {
      // errore rete
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const post = async (path, label, body) => {
    setBusy(label);
    try {
      await fetch(`${API}${path}`, { method: "POST", headers, body: body ? JSON.stringify(body) : undefined });
    } catch {
      // riprovare
    }
    await load();
    setBusy("");
  };

  const del = async (path, label) => {
    setBusy(label);
    try {
      await fetch(`${API}${path}`, { method: "DELETE", headers });
    } catch {
      // riprovare
    }
    await load();
    setBusy("");
  };

  const copy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const statusChip = (s) => {
    const map = {
      pending: "text-amber-300 border-amber-300/40",
      draft: "text-amber-300 border-amber-300/40",
      applied: "text-neon-cyan border-neon-cyan/40",
      published: "text-neon-cyan border-neon-cyan/40",
      rejected: "text-neon-magenta border-neon-magenta/40",
    };
    const label = { pending: "Da approvare", draft: "Bozza", applied: "Applicata", published: "Pubblicato", rejected: "Rifiutata" }[s] || s;
    return <span className={`text-[10px] hud-label uppercase border rounded-full px-2.5 py-1 ${map[s] || ""}`}>{label}</span>;
  };

  const drafts = articles.filter((a) => a.status === "draft");
  const published = articles.filter((a) => a.status === "published");

  return (
    <div className="flex flex-col gap-10" data-testid="seo-dashboard">

      {/* ——— Pagine ——— */}
      <section className="glass-panel rounded-2xl p-6" data-testid="seo-pages">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileSearch size={18} className="text-neon-cyan" />
            <h2 className="font-display font-bold">Analisi pagine</h2>
          </div>
          <button
            onClick={() => post("/seo/analyze", "analyze")}
            disabled={busy === "analyze"}
            data-testid="seo-analyze-button"
            className="flex items-center gap-2 rounded-full border border-neon-cyan/40 px-4 py-2 text-xs text-neon-cyan hover:bg-neon-cyan/10 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={busy === "analyze" ? "animate-spin" : ""} />
            {busy === "analyze" ? "Analisi in corso (30s)..." : "Analizza ora"}
          </button>
        </div>
        {analyses.length === 0 && <p className="text-xs text-white/40">Nessuna analisi ancora. Premi "Analizza ora".</p>}
        <div className="flex flex-col gap-4">
          {analyses.map((a) => (
            <div key={a.id} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`seo-analysis-${a.id}`}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <p className="font-semibold text-sm">{a.name}</p>
                {statusChip(a.status)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="hud-label text-[10px] uppercase text-white/35 mb-1.5">Attuale</p>
                  <p className="text-white/60">{a.current_title || "—"}</p>
                  <p className="text-white/40 mt-1">{a.current_description || "—"}</p>
                </div>
                <div>
                  <p className="hud-label text-[10px] uppercase text-neon-cyan mb-1.5">Proposta IA</p>
                  <p className="text-white/85">{a.proposed_title}</p>
                  <p className="text-white/55 mt-1">{a.proposed_description}</p>
                  <p className="text-white/40 mt-1">H1: {a.proposed_h1} · Schema: {a.schema_type}</p>
                </div>
              </div>
              {a.status === "pending" && (
                <div className="flex gap-3 mt-4">
                  <button onClick={() => post(`/seo/analyses/${a.id}/approve`, `ap-${a.id}`)} data-testid={`seo-approve-${a.id}`} className="flex items-center gap-1.5 rounded-full bg-neon-cyan/15 border border-neon-cyan/40 px-4 py-1.5 text-xs text-neon-cyan hover:bg-neon-cyan/25">
                    <Check size={13} /> Approva e applica
                  </button>
                  <button onClick={() => post(`/seo/analyses/${a.id}/reject`, `rj-${a.id}`)} data-testid={`seo-reject-${a.id}`} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs text-white/60 hover:text-neon-magenta">
                    <X size={13} /> Rifiuta
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ——— Keyword ——— */}
      <section className="glass-panel rounded-2xl p-6" data-testid="seo-keywords">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <KeySquare size={18} className="text-neon-violet" />
            <h2 className="font-display font-bold">Parole chiave</h2>
          </div>
          <button
            onClick={() => post("/seo/keywords/suggest", "suggest")}
            disabled={busy === "suggest"}
            data-testid="seo-suggest-button"
            className="flex items-center gap-2 rounded-full border border-neon-violet/40 px-4 py-2 text-xs text-neon-violet hover:bg-neon-violet/10 transition-colors disabled:opacity-50"
          >
            <Sparkles size={13} /> {busy === "suggest" ? "Claude sta pensando..." : "Proponi con IA"}
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (newKw.trim()) { post("/seo/keywords", "addkw", { keyword: newKw.trim() }); setNewKw(""); } }}
          className="flex gap-2 mb-5"
        >
          <input
            value={newKw}
            onChange={(e) => setNewKw(e.target.value)}
            placeholder="Aggiungi una parola chiave, es. gestionale su misura lugano"
            data-testid="seo-keyword-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-neon-cyan/60"
          />
          <button type="submit" data-testid="seo-keyword-add" className="btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-5 py-2.5 text-xs font-semibold text-void">
            Aggiungi
          </button>
        </form>
        <div className="flex flex-wrap gap-2">
          {keywords.map((k) => (
            <span key={k.id} data-testid={`keyword-${k.id}`} className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs ${k.approved ? "border-neon-cyan/40 text-neon-cyan" : "border-amber-300/40 text-amber-300"}`}>
              {k.keyword}
              {!k.approved && (
                <button onClick={() => post(`/seo/keywords/${k.id}/approve`, `ka-${k.id}`)} data-testid={`keyword-approve-${k.id}`} className="hover:text-white" aria-label="Approva keyword">
                  <Check size={12} />
                </button>
              )}
              <button onClick={() => del(`/seo/keywords/${k.id}`, `kd-${k.id}`)} data-testid={`keyword-delete-${k.id}`} className="hover:text-neon-magenta" aria-label="Elimina keyword">
                <X size={12} />
              </button>
            </span>
          ))}
          {keywords.length === 0 && <p className="text-xs text-white/40">Nessuna keyword. Aggiungile tu o falle proporre all'IA.</p>}
        </div>
      </section>

      {/* ——— Articoli ——— */}
      <section className="glass-panel rounded-2xl p-6" data-testid="seo-articles">
        <div className="flex items-center gap-3 mb-5">
          <Newspaper size={18} className="text-neon-magenta" />
          <h2 className="font-display font-bold">Articoli del blog</h2>
        </div>
        <div className="flex gap-2 mb-6 flex-wrap">
          <select
            value={genKw}
            onChange={(e) => setGenKw(e.target.value)}
            data-testid="seo-article-keyword-select"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-neon-cyan/60 text-white"
          >
            <option value="">Scegli keyword approvata...</option>
            {keywords.filter((k) => k.approved).map((k) => (
              <option key={k.id} value={k.keyword}>{k.keyword}</option>
            ))}
          </select>
          <button
            onClick={() => genKw && post("/seo/articles/generate", "gen", { keyword: genKw })}
            disabled={!genKw || busy === "gen"}
            data-testid="seo-generate-button"
            className="btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-5 py-2.5 text-xs font-semibold text-void disabled:opacity-50"
          >
            {busy === "gen" ? "Scrittura in corso (60s)..." : "Genera bozza ora"}
          </button>
        </div>
        <p className="text-[10px] text-white/30 mb-5">Il cron settimanale (lunedì 9:00) genera in automatico una bozza dalla prima keyword approvata libera. Niente viene pubblicato senza il tuo ok.</p>

        {drafts.length > 0 && <p className="hud-label text-[10px] uppercase text-amber-300 mb-3">Da approvare</p>}
        <div className="flex flex-col gap-4 mb-6">
          {drafts.map((a) => (
            <div key={a.id} className="bg-white/5 border border-amber-300/20 rounded-xl p-5" data-testid={`article-draft-${a.id}`}>
              <p className="font-semibold text-sm mb-1">{a.title}</p>
              <p className="text-xs text-white/50 mb-2">{a.excerpt}</p>
              <p className="text-[10px] text-white/35 mb-4">Meta: {a.meta_title} · {a.meta_description}</p>
              <div className="flex gap-3">
                <button onClick={() => post(`/seo/articles/${a.id}/approve`, `aa-${a.id}`)} data-testid={`article-approve-${a.id}`} className="flex items-center gap-1.5 rounded-full bg-neon-cyan/15 border border-neon-cyan/40 px-4 py-1.5 text-xs text-neon-cyan hover:bg-neon-cyan/25">
                  <Check size={13} /> Approva e pubblica
                </button>
                <button onClick={() => post(`/seo/articles/${a.id}/reject`, `ar-${a.id}`)} data-testid={`article-reject-${a.id}`} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs text-white/60 hover:text-neon-magenta">
                  <X size={13} /> Rifiuta
                </button>
              </div>
            </div>
          ))}
        </div>

        {published.length > 0 && <p className="hud-label text-[10px] uppercase text-neon-cyan mb-3">Pubblicati</p>}
        <div className="flex flex-col gap-4">
          {published.map((a) => (
            <div key={a.id} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`article-pub-${a.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:text-neon-cyan transition-colors">{a.title}</a>
                <span className="text-[10px] text-white/35">{a.published_at?.slice(0, 10)}</span>
              </div>
              {a.linkedin_post ? (
                <div className="mt-3 bg-white/5 border border-neon-violet/25 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="hud-label text-[10px] uppercase text-neon-violet">Post LinkedIn proposto</p>
                    <button onClick={() => copy(a.linkedin_post, a.id)} data-testid={`linkedin-copy-${a.id}`} className="flex items-center gap-1 text-[10px] text-white/50 hover:text-neon-cyan">
                      <Copy size={11} /> {copied === a.id ? "Copiato!" : "Copia"}
                    </button>
                  </div>
                  <p className="text-xs text-white/70 whitespace-pre-wrap leading-relaxed">{a.linkedin_post}</p>
                </div>
              ) : (
                <p className="text-[10px] text-white/30 mt-2">Post LinkedIn in generazione...</p>
              )}
            </div>
          ))}
          {articles.length === 0 && <p className="text-xs text-white/40">Nessun articolo ancora. Approva una keyword e genera la prima bozza.</p>}
        </div>
      </section>

      {/* ——— Search Console ——— */}
      <section className="glass-panel rounded-2xl p-6" data-testid="seo-gsc">
        <div className="flex items-center gap-3 mb-5">
          <BarChart3 size={18} className="text-neon-cyan" />
          <h2 className="font-display font-bold">Google Search Console</h2>
        </div>
        {!gsc || !gsc.configured ? (
          <div className="text-xs text-white/60 leading-relaxed flex flex-col gap-2" data-testid="gsc-setup">
            <p>Per vedere posizioni e clic servono 3 passaggi (una tantum):</p>
            <ol className="list-decimal pl-5 flex flex-col gap-1.5">
              <li>Verifica la proprietà del dominio su <span className="text-neon-cyan">search.google.com/search-console</span> (dopo il collegamento del dominio)</li>
              <li>Crea un <strong className="text-white">service account</strong> su Google Cloud e aggiungi la sua email come utente della proprietà Search Console</li>
              <li>Mandami il file JSON del service account: lo configuro io sul server</li>
            </ol>
            <p className="text-white/35 mt-2">Nota: Google non offre un'API di "invio URL" per siti normali — gli URL vengono inviati automaticamente tramite la sitemap.xml che l'agente rigenera a ogni pubblicazione.</p>
          </div>
        ) : gsc.error ? (
          <p className="text-xs text-neon-magenta">Errore Search Console: {gsc.error}</p>
        ) : (
          <div data-testid="gsc-data">
            <p className="hud-label text-[10px] uppercase text-white/35 mb-3">Ultimi 28 giorni — query principali</p>
            <div className="flex flex-col gap-2">
              {gsc.rows.length === 0 && <p className="text-xs text-white/40">Nessun dato ancora: Google impiega qualche giorno dopo la verifica.</p>}
              {gsc.rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-white/5 rounded-lg px-4 py-2.5">
                  <span className="text-white/80">{r.keys[0]}</span>
                  <span className="text-white/50 flex gap-4">
                    <span><Globe size={11} className="inline mr-1" />{r.impressions} imp.</span>
                    <span className="text-neon-cyan">{r.clicks} clic</span>
                    <span>pos. {r.position?.toFixed(1)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SeoDashboard;
